import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    Grid,
    Chip,
    Avatar,
    Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StraightenIcon from '@mui/icons-material/Straighten';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import './MannequinDetailModal.scss';

interface MannequinDetailModalProps {
    open: boolean;
    onClose: () => void;
    mannequin: any;
}

export const MannequinDetailModal: React.FC<MannequinDetailModalProps> = ({ open, onClose, mannequin }) => {
    const { t } = useTranslation();

    if (!mannequin) return null;

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        try {
            return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
        } catch {
            return '-';
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="mannequin-detail-modal">
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="div" fontWeight="600">
                    {t('mannequin.detailTitle')}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={3}>
                    {/* Left Side: Photo */}
                    <Grid
                        size={{
                            xs: 12,
                            md: 5,
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                height: '400px',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                bgcolor: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                            }}
                        >
                            {mannequin.photoUrl ? (
                                <img
                                    src={mannequin.photoUrl}
                                    alt={mannequin.name}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                />
                            ) : (
                                <Avatar
                                    sx={{
                                        width: 100,
                                        height: 100,
                                        bgcolor: mannequin.isAdult ? '#1976d2' : '#ff9800',
                                        fontSize: '40px',
                                    }}
                                >
                                    {mannequin.name?.charAt(0)}
                                </Avatar>
                            )}
                            <Chip
                                label={mannequin.isAdult ? t('mannequin.adult') : t('mannequin.child')}
                                color={mannequin.isAdult ? 'primary' : 'warning'}
                                sx={{
                                    position: 'absolute',
                                    top: 16,
                                    right: 16,
                                    fontWeight: 600,
                                }}
                            />
                        </Box>
                    </Grid>

                    {/* Right Side: Info */}
                    <Grid
                        size={{
                            xs: 12,
                            md: 7,
                        }}
                    >
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h4" gutterBottom fontWeight="700" color="primary">
                                {mannequin.name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mb: 2 }}>
                                <BusinessIcon sx={{ fontSize: '18px' }} />
                                <Typography variant="subtitle1">{mannequin.agencyName || '-'}</Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        <Grid container spacing={2}>
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                            >
                                <Box className="detail-item">
                                    <Typography variant="caption" color="text.secondary">
                                        {t('mannequin.passNo')}
                                    </Typography>
                                    <Typography variant="body1" fontWeight="500">
                                        {mannequin.passNo || '-'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                            >
                                <Box className="detail-item">
                                    <Typography variant="caption" color="text.secondary">
                                        {t('mannequin.status')}
                                    </Typography>
                                    <Chip
                                        label={mannequin.active ? t('general.active') : t('general.passive')}
                                        color={mannequin.active ? 'success' : 'default'}
                                        size="small"
                                        variant="outlined"
                                    />
                                </Box>
                            </Grid>

                            <Grid
                                size={{
                                    xs: 12,
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                                >
                                    <StraightenIcon sx={{ fontSize: '16px' }} />
                                    {t('mannequin.measurements')}
                                </Typography>
                            </Grid>

                            {mannequin.isAdult && (
                                <>
                                    <Grid
                                        size={{
                                            xs: 4,
                                        }}
                                    >
                                        <Box className="measurement-box">
                                            <span className="label">{t('mannequin.chest')}</span>
                                            <span className="value">{mannequin.chest}</span>
                                        </Box>
                                    </Grid>
                                    <Grid
                                        size={{
                                            xs: 4,
                                        }}
                                    >
                                        <Box className="measurement-box">
                                            <span className="label">{t('mannequin.waist')}</span>
                                            <span className="value">{mannequin.waist}</span>
                                        </Box>
                                    </Grid>
                                    <Grid
                                        size={{
                                            xs: 4,
                                        }}
                                    >
                                        <Box className="measurement-box">
                                            <span className="label">{t('mannequin.hip')}</span>
                                            <span className="value">{mannequin.hip}</span>
                                        </Box>
                                    </Grid>
                                </>
                            )}

                            <Grid
                                size={{
                                    xs: 4,
                                }}
                            >
                                <Box className="measurement-box">
                                    <span className="label">{t('mannequin.length')}</span>
                                    <span className="value">{mannequin.length} cm</span>
                                </Box>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 4,
                                }}
                            >
                                <Box className="measurement-box">
                                    <span className="label">{t('mannequin.shoeSize')}</span>
                                    <span className="value">{mannequin.shoeSize}</span>
                                </Box>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 4,
                                }}
                            >
                                <Box className="measurement-box">
                                    <span className="label">{t('mannequin.weight')}</span>
                                    <span className="value">{mannequin.weight ? `${mannequin.weight} kg` : '-'}</span>
                                </Box>
                            </Grid>

                            <Grid
                                size={{
                                    xs: 12,
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                                >
                                    <CalendarTodayIcon sx={{ fontSize: '16px' }} />
                                    {t('mannequin.otherInfo')}
                                </Typography>
                            </Grid>

                            <Grid
                                size={{
                                    xs: 6,
                                }}
                            >
                                <Box className="detail-item">
                                    <Typography variant="caption" color="text.secondary">
                                        {t('mannequin.lastShotDate')}
                                    </Typography>
                                    <Typography variant="body2">{formatDate(mannequin.lastShotDate)}</Typography>
                                </Box>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                            >
                                <Box className="detail-item">
                                    <Typography variant="caption" color="text.secondary">
                                        {t('mannequin.isAi')}
                                    </Typography>
                                    <Typography variant="body2">
                                        {mannequin.isAi ? t('general.yes') : t('general.no')}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};
